import Icon from '../../common/components/Icon';
import Checkbox from '../../components/Checkbox/Checkbox';
import React, { useEffect, useState } from 'react';

export interface FilterItem {
  name: string;
  values: string[];
}

export interface FilterProps {
  title: string;
  applyButton: {
    label: string;
    onClick: (selectedItems: Array<FilterItem>) => void;
  };
  resetButton: {
    label: string;
    onClick: () => void;
  };
  items: FilterItem[];
}

type CheckedItems = {
  [itemName: string]: string[];
};

const Filter: React.FC<FilterProps> = ({ title, applyButton, resetButton, items }) => {
  const [openItems, setOpenItems] = useState<boolean[]>(Array(items.length).fill(false));
  const [checkedItems, setCheckedItems] = useState<CheckedItems>({});

  useEffect(() => {
    const url = new URL(window.location.href);
    const newCheckedItems: { [key: string]: string[] } = {};

    url.searchParams.forEach((value, key) => {
      const match = key.match(/^attributes\[(.+)\]$/);
      if (match) {
        const attributeName = match[1];
        newCheckedItems[attributeName] = value.split(',');
      }
    });

    setCheckedItems(newCheckedItems);
  }, []);

  const toggleItem = (index: number) => {
    setOpenItems((prev) => {
      const newOpenItems = [...prev];
      newOpenItems[index] = !newOpenItems[index];
      return newOpenItems;
    });
  };

  const handleCheckboxChange = (itemName: string, value: string) => {
    setCheckedItems((prev) => {
      const prevValues = prev[itemName] || [];
      const isChecked = prevValues.includes(value);

      const newValues = isChecked ? prevValues.filter((v) => v !== value) : [...prevValues, value];

      return {
        ...prev,
        [itemName]: newValues,
      };
    });
  };

  const handleApplyClick = () => {
    const selectedItems = Object.entries(checkedItems).map(([name, values]) => ({
      name,
      values,
    }));

    applyButton.onClick(selectedItems);
  };

  const handleResetClick = () => {
    setCheckedItems({});
    resetButton.onClick();
  };

  return (
    <div className="ecom-filter">
      <h4 className="ecom-filter__title">{title}</h4>

      <div className="ecom-filter__items">
        {items.map((item, index) => (
          <div className="ecom-filter__item" key={index}>
            <div className="ecom-filter__item-header" onClick={() => toggleItem(index)}>
              <p className="link-16">{item.name}</p>
              {openItems[index] ? <Icon name="minus" size="medium" /> : <Icon name="plus" size="medium" />}
            </div>

            <div className={`ecom-filter__options ${openItems[index] ? 'show open' : ''}`}>
              {item.values.map((option, index) => (
                <div className="ecom-filter__option" key={option}>
                  <label htmlFor={option} className="link-16" onClick={() => handleCheckboxChange(item.name, option)}>
                    {option}
                  </label>
                  <Checkbox
                    id={option}
                    checked={checkedItems[item.name]?.includes(option) || false}
                    onChange={() => handleCheckboxChange(item.name, option)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="ecom-filter__buttons">
        <button className="ecom-filter__button button button--primary button--large" onClick={handleApplyClick}>
          {applyButton.label}
        </button>
        <button className="ecom-filter__button--reset link-16" onClick={handleResetClick}>
          {resetButton.label}
        </button>
      </div>
    </div>
  );
};

export default Filter;
